import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useStatusBar } from "@/hooks/useStatusBar";
import KinAvatar from "./KinAvatar";

interface NameCaptureModalProps {
  /** Called after a save succeeds OR when she skips -- either way, the
   *  caller should never show this again for this account. */
  onComplete: (skipped: boolean) => void;
}

/**
 * One-time "what's your name" popup. Two entry points, same component:
 * fires after the onboarding tutorial for brand-new signups (before Mom
 * Type Selector), and as a one-time nudge for existing accounts with no
 * name on file. Writes to the same first_name auth-metadata field #164
 * and the Settings "Add your name" editor both use.
 */
const NameCaptureModal = ({ onComplete }: NameCaptureModalProps) => {
  useStatusBar({ color: "F0F0FF", style: "dark" });
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [visible, setVisible] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
  }, []);

  const animateOut = (cb: () => void) => {
    setVisible(false);
    setTimeout(cb, 300);
  };

  const handleSkip = () => {
    if (saving) return;
    animateOut(() => onComplete(true));
  };

  const handleContinue = async () => {
    if (saving) return;
    const trimmed = name.trim();
    if (!trimmed) {
      handleSkip();
      return;
    }
    setSaving(true);
    try {
      const { error } = await supabase.auth.updateUser({ data: { first_name: trimmed } });
      if (error) throw error;
      animateOut(() => onComplete(false));
    } catch (e) {
      console.error("[NameCaptureModal] save failed", e);
      toast.error("Couldn't save. You can add your name anytime in Settings.");
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center transition-all duration-300"
      style={{
        backgroundColor: visible ? "rgba(28, 26, 62, 0.6)" : "rgba(28, 26, 62, 0)",
        backdropFilter: visible ? "blur(4px)" : "blur(0px)",
      }}
    >
      <div
        className="relative w-full mx-4 bg-white transition-all duration-400 ease-out"
        style={{
          maxWidth: 440,
          borderRadius: 20,
          padding: "28px 20px",
          boxShadow: "0 24px 60px rgba(28, 26, 62, 0.25)",
          opacity: visible ? 1 : 0,
          transform: visible ? "scale(1)" : "scale(0.95)",
        }}
      >
        <div className="flex flex-col items-center">
          <div className="animate-float mb-4">
            <KinAvatar size="md" variant="dark" />
          </div>

          <h1
            className="text-center"
            style={{ fontFamily: "'DM Serif Display', serif", fontSize: 24, color: "#1C1A3E", marginBottom: 4 }}
          >
            Hey mama, nice to meet you!
          </h1>
          <p
            className="text-center"
            style={{ fontFamily: "'Instrument Serif', serif", fontStyle: "italic", fontSize: 18, color: "#5050C8", marginBottom: 18 }}
          >
            What's your name?
          </p>

          <input
            ref={inputRef}
            autoFocus
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleContinue();
            }}
            placeholder="Your first name"
            maxLength={40}
            disabled={saving}
            className="w-full bg-white"
            style={{
              border: "1.5px solid #C8C8F4",
              borderRadius: 14,
              padding: "13px 16px",
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 16,
              color: "#1C1A3E",
              outline: "none",
            }}
          />
          <p
            className="text-center"
            style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 12, color: "#54525C", marginTop: 8, lineHeight: 1.4 }}
          >
            You can always change this later in Settings. It also personalizes your Kin email address.
          </p>

          <button
            onClick={handleContinue}
            disabled={saving}
            className="w-full mt-5 transition-all duration-200 flex items-center justify-center"
            style={{
              height: 48,
              borderRadius: 14,
              backgroundColor: "#5050C8",
              color: "#FFFFFF",
              fontFamily: "'DM Serif Display', serif",
              fontSize: 17,
              border: "none",
              cursor: saving ? "not-allowed" : "pointer",
              opacity: saving ? 0.7 : 1,
            }}
          >
            {saving ? "Saving…" : "Continue"}
          </button>

          <button
            onClick={handleSkip}
            disabled={saving}
            className="mt-3 hover:underline transition-all"
            style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 14,
              color: "#54525C",
              background: "none",
              border: "none",
              cursor: saving ? "default" : "pointer",
            }}
          >
            Skip for now
          </button>
        </div>
      </div>
    </div>
  );
};

export default NameCaptureModal;
